/**
 * Settings -> Notifications -> "Send a test notification" (TRUS-668).
 *
 * POST /api/notifications/test-desktop writes a bell row for the caller and
 * pushes to their registered devices with the same tag. This then reports,
 * separately, which path reached THIS computer:
 *
 *  - Push: the service worker says it showed (or received) the push for that
 *    row; or why not (no registration here, not set up, refused, never arrived).
 *  - In-app check: staff - the notification poll (StaffNotificationsHost) is
 *    asked to poll now and reports what it did with the row; members - the
 *    page shows it itself, the way the member notifier does.
 *
 * Works in the desktop app too (no push there; the in-app check is its path).
 */
import { useState } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { apiClient } from '@/services/apiClient';
import { memberApiFetch } from '@/lib/memberAuth';
import {
  NOTIFICATION_SHOWN_TYPE,
  isTauriApp,
  notificationRowTag,
  showCollapsingNotification,
} from '@/lib/browserNotifications';
import { desktopNotifications } from '@/services/desktopNotifications';
import { goToPushMasterSwitch } from '@/components/notifications/PushMasterSetting';
import {
  refreshNotificationSummary,
  waitForBellOutcome,
  type BellOutcome,
} from '@/lib/notificationSummaryStore';

export const PUSH_WAIT_MS = 15_000;
export const BELL_WAIT_MS = 20_000;

export type Portal = 'staff' | 'member';

export interface DesktopTestResponse {
  notification_id: number;
  tag: string;
  push: {
    configured: boolean;
    sent: number;
    failed: number;
    skipped: number;
    /** TRUS-697: "Push notifications on all your devices" is off - nothing was pushed anywhere. */
    push_off?: boolean;
    detail?: string;
  };
}

export type PushResult = 'push_off' | 'arrived' | 'not_registered' | 'not_configured' | 'desktop_app' | 'failed' | 'missing';
export type BellResult = BellOutcome | 'missing';

export interface SelfTestResult {
  push: PushResult;
  bell: BellResult;
}

/** Collects what the service worker reports while the test runs. */
function watchServiceWorker() {
  const seen: Array<{ tag?: string | null; notification_id?: unknown }> = [];
  const waiters: Array<() => void> = [];
  const sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined;
  const onMessage = (event: MessageEvent) => {
    const msg = event.data as
      | { type?: string; tag?: string | null; notification_id?: unknown; payload?: { notification_id?: unknown } }
      | null;
    if (!msg) return;
    if (msg.type === NOTIFICATION_SHOWN_TYPE) seen.push({ tag: msg.tag, notification_id: msg.notification_id });
    else if (msg.type === 'WEAVEIN_PUSH') seen.push({ notification_id: msg.payload?.notification_id });
    else return;
    waiters.splice(0).forEach((w) => w());
  };
  try {
    sw?.addEventListener('message', onMessage);
  } catch {
    /* no service worker container */
  }
  const matches = (id: number) =>
    seen.some((s) => s.tag === notificationRowTag(id) || String(s.notification_id ?? '') === String(id));
  return {
    wait(id: number, timeoutMs: number): Promise<boolean> {
      if (matches(id)) return Promise.resolve(true);
      return new Promise((resolve) => {
        const timer = setTimeout(() => resolve(matches(id)), timeoutMs);
        const check = () => {
          if (matches(id)) {
            clearTimeout(timer);
            resolve(true);
          } else waiters.push(check);
        };
        waiters.push(check);
      });
    },
    stop() {
      try {
        sw?.removeEventListener('message', onMessage);
      } catch {
        /* ignore */
      }
    },
  };
}

async function requestTest(portal: Portal): Promise<DesktopTestResponse> {
  if (portal === 'member') {
    const res = await memberApiFetch('/api/notifications/test-desktop', {
      method: 'POST',
      body: JSON.stringify({ portal }),
    });
    if (!res.ok) throw new Error(`test-desktop ${res.status}`);
    return res.json();
  }
  const res = await apiClient.post('/api/notifications/test-desktop', { portal });
  return res.data as DesktopTestResponse;
}

/** The member portal's in-app path: show the row here, collapsing with the push. */
async function showLocally(id: number, title: string, body: string): Promise<BellOutcome> {
  const collapsed = await showCollapsingNotification({
    title,
    body,
    tag: notificationRowTag(id),
    url: '/member/notifications',
    data: { type: 'notification', notification_id: id },
  });
  if (collapsed === 'shown' || collapsed === 'duplicate') return collapsed;
  await desktopNotifications.showNotification({ title, body, tag: notificationRowTag(id) });
  return desktopNotifications.getPermissionState() === 'granted' ? 'shown' : 'blocked';
}

export async function runNotificationSelfTest(
  portal: Portal,
  text: { title: string; body: string },
  waits = { push: PUSH_WAIT_MS, bell: BELL_WAIT_MS },
): Promise<SelfTestResult> {
  const watcher = watchServiceWorker();
  try {
    const res = await requestTest(portal);
    const id = res.notification_id;
    const { configured, sent, failed, skipped } = res.push;

    let pushPromise: Promise<PushResult>;
    // TRUS-697: the server skipped every device because push is off in settings.
    if (res.push.push_off) pushPromise = Promise.resolve('push_off');
    else if (isTauriApp()) pushPromise = Promise.resolve('desktop_app');
    else if (!configured) pushPromise = Promise.resolve('not_configured');
    else if (sent === 0 && failed === 0 && skipped === 0) pushPromise = Promise.resolve('not_registered');
    else if (sent === 0 && failed > 0) pushPromise = Promise.resolve('failed');
    else pushPromise = watcher.wait(id, waits.push).then((ok) => (ok ? 'arrived' : 'missing'));

    let bell: BellResult;
    if (portal === 'staff') {
      void refreshNotificationSummary();
      bell = (await waitForBellOutcome(id, waits.bell)) ?? 'missing';
    } else {
      // Give a push a moment first, so the local one collapses into it.
      await Promise.race([pushPromise, new Promise((r) => setTimeout(r, Math.min(3000, waits.push)))]);
      bell = await showLocally(id, text.title, text.body);
    }
    return { push: await pushPromise, bell };
  } finally {
    watcher.stop();
  }
}

export function NotificationSelfTest({ portal }: { portal: Portal }) {
  const { t } = useAppTranslation();
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<SelfTestResult | null>(null);
  const [failed, setFailed] = useState(false);

  const pushText: Record<PushResult, string> = {
    push_off: t(
      'messaging.selfTest.pushOff',
      'Push: off in your settings ("Push notifications on all your devices"), so nothing was sent to your phones or browsers.',
    ),
    arrived: t('messaging.selfTest.pushArrived', 'Push: arrived.'),
    not_registered: t(
      'messaging.selfTest.pushNotRegistered',
      "Push: this browser isn't registered. Turn on browser notifications above.",
    ),
    not_configured: t('messaging.selfTest.pushNotConfigured', 'Push: not set up for your organization.'),
    desktop_app: t(
      'messaging.selfTest.pushDesktopApp',
      'Push: not used in the desktop app. The app checks for new notifications itself.',
    ),
    failed: t(
      'messaging.selfTest.pushFailed',
      'Push: the notification service refused it. Turn browser notifications off and on again.',
    ),
    missing: t(
      'messaging.selfTest.pushMissing',
      "Push: sent, but it didn't reach this browser within 15 seconds. Your network may be blocking Google's notification service.",
    ),
  };
  const bellText: Record<BellResult, string> = {
    shown: t('messaging.selfTest.bellShown', 'In-app check: notification shown.'),
    duplicate: t('messaging.selfTest.bellDuplicate', 'In-app check: found it (the push had already shown it).'),
    blocked: t(
      'messaging.selfTest.bellBlocked',
      "In-app check: notifications are blocked. Allow them for this site, or for WeaveIn in your computer's notification settings.",
    ),
    skipped: t('messaging.selfTest.bellSkipped', 'In-app check: desktop notifications are switched off in your preferences.'),
    missing: t('messaging.selfTest.bellMissing', 'In-app check: nothing arrived. Reload the page and try again.'),
  };

  const run = async () => {
    setRunning(true);
    setFailed(false);
    setResult(null);
    try {
      setResult(
        await runNotificationSelfTest(portal, {
          title: t('messaging.selfTest.notificationTitle', 'WeaveIn test notification'),
          body: t('messaging.selfTest.notificationBody', 'If you can see this, notifications work on this computer.'),
        }),
      );
    } catch {
      setFailed(true);
    } finally {
      setRunning(false);
    }
  };

  const ok = (v: string) => v === 'arrived' || v === 'shown' || v === 'duplicate';

  return (
    <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700" data-testid="notification-self-test">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold">{t('messaging.selfTest.title', 'Test notifications')}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t(
              'messaging.selfTest.description',
              'Sends a test notification to this computer both ways (push and the in-app check) and shows which arrived.',
            )}
          </p>
        </div>
        <button
          type="button"
          disabled={running}
          onClick={() => void run()}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium hover:bg-gray-50 disabled:opacity-60 dark:border-gray-600 dark:hover:bg-gray-800"
        >
          {running
            ? t('messaging.selfTest.running', 'Testing...')
            : t('messaging.selfTest.send', 'Send a test notification')}
        </button>
      </div>
      {result && (
        <ul className="mt-3 space-y-1 text-sm" role="status" data-testid="notification-self-test-result">
          <li
            className={ok(result.push) ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}
            data-testid="notification-self-test-push"
          >
            {pushText[result.push]}
            {result.push === 'push_off' && portal === 'staff' && (
              <>
                {' '}
                <button
                  type="button"
                  onClick={goToPushMasterSwitch}
                  className="font-medium underline underline-offset-2"
                  data-testid="notification-self-test-go-to-switch"
                >
                  {t('messaging.selfTest.goToPushSwitch', 'Go to the switch')}
                </button>
              </>
            )}
          </li>
          <li className={ok(result.bell) ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}>
            {bellText[result.bell]}
          </li>
          <li className="text-gray-600 dark:text-gray-400">
            {t(
              'messaging.selfTest.focusHint',
              "Nothing on screen even though it says shown? Check your computer's Focus / Do Not Disturb settings and the notification settings for your browser or the WeaveIn app.",
            )}
          </li>
        </ul>
      )}
      {failed && (
        <p className="mt-3 text-sm text-red-600 dark:text-red-400" role="alert">
          {t('messaging.selfTest.error', "Couldn't send the test. Try again.")}
        </p>
      )}
    </div>
  );
}

export default NotificationSelfTest;
